// src/services/CloudApiService.js
import { callApi } from  'services/ApiCommon';
import commClient, { isCommTransport, isCommAvailable, isCommMsgId } from 'services/commClient';
import { SCHEMA_VERSION_KEY, CURRENT_SCHEMA_VERSION } from 'services/modelSchema';
import { throwIfMaintenanceResponse, toMaintenanceErrorIfSwitching } from 'services/maintenance';

const REACT_APP_BE_API = process.env.REACT_APP_BE_API;

// ============================================================
// 메시지 / 알림 통합 (MDLRMDL_MSG)
//   레거시 호환: endpoint URL 은 /myInvites, /markInviteRead 유지
//   내부적으로는 VW_MDLRMDL_MSG 에서 조회 (msg_tcd: I=초대 / S=제출 / M=쪽지)
// ============================================================

/**
 * 내 메시지 목록 조회.
 * @param msgTcds: ['I','S','M']  지정 시 해당 유형만 (미지정 = 전체)
 */
export const fetchMyMessages = async ({ offset = 0, limit = 30, msgTcds } = {}) => {
    const body = { offset, limit };
    if (Array.isArray(msgTcds) && msgTcds.length > 0) body.msgTcds = msgTcds;
    const response = await fetch(`${REACT_APP_BE_API}/qbase/api/myInvites`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('메시지 목록 조회 실패');
    const legacy = await response.json();
    // 응답: { items, hasMore, unread? }

    // ── COMM 전환(M1.5) — 쪽지(M)는 QBase 공통 API 에서 온다 ──────────────
    // 레거시 목록은 그대로 두고(과거 M 행·I/S/A 는 여전히 저기 있다) COMM 의 M 을
    // 합친다. 새 발송은 COMM 으로만 가므로 이중 집계는 없다.
    // isCommAvailable: 권한 없음(403)을 한 번 확인했으면 더 시도하지 않는다.
    // 벨은 주기적으로 다시 부르므로, 그러지 않으면 403 이 폴링마다 반복된다.
    if (isCommAvailable()) {
        try {
            const [commItems, commUnread] = await Promise.all([
                commClient.fetchRecent(limit),
                commClient.fetchUnreadTotal(),
            ]);
            const merged = [...(legacy.items || []), ...commItems]
                .sort((a, b) => String(b.sentDt || '').localeCompare(String(a.sentDt || '')));
            return {
                items: merged.slice(0, limit),
                hasMore: !!legacy.hasMore || merged.length > limit,
                unread: (legacy.unread || 0) + commUnread,
            };
        } catch (e) {
            // COMM 이 죽어도 레거시 알림(I/S)은 계속 보인다 — 단, 조용히 지나가지 않고 남긴다
            console.error('[COMM] 쪽지 조회 실패 — 레거시 목록만 표시:', e.message);
            return legacy;
        }
    }
    return legacy;
};
// 레거시 alias — 점진 마이그레이션용
export const fetchMyInvites = fetchMyMessages;

/**
 * 모델 공유(=제출) 시 그룹 관리자에게 알림 row INSERT.
 * 발신자 본인이 관리자이면 backend 가 no-op 처리.
 */
export const notifySubmit = async ({ mdlrmdlShrgrpId, shrgrpAsmtNm, mdlrmdlId, senderMessage }) => {
    const response = await fetch(`${REACT_APP_BE_API}/qbase/api/notifySubmit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mdlrmdlShrgrpId, shrgrpAsmtNm, mdlrmdlId, senderMessage }),
    });
    if (!response.ok) throw new Error('제출 알림 생성 실패');
    return await response.json();
};

/**
 * 메시지 읽음 처리.
 * @param msgIds: 비우면 본인 미읽 전체 / 지정 시 해당 ID 만
 */
export const markMessageRead = async (msgIds = []) => {
    // COMM 전환(M1.5): QCM 채번 ID 는 방 커서로, 나머지는 레거시로.
    // 빈 배열(모두 읽음)은 양쪽 다 보낸다.
    if (isCommAvailable()) {
        const commIds = msgIds.filter(isCommMsgId);
        const legacyIds = msgIds.filter((id) => !isCommMsgId(id));
        if (commIds.length > 0 || msgIds.length === 0) {
            await commClient.markRead(commIds);
        }
        if (msgIds.length > 0 && legacyIds.length === 0) {
            return { marked: commIds.length };
        }
        msgIds = legacyIds;   // 레거시 몫만 아래로
    }
    const response = await fetch(`${REACT_APP_BE_API}/qbase/api/markInviteRead`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inviteIds: msgIds }),  // backend payload 키는 inviteIds 유지
    });
    if (!response.ok) throw new Error('읽음 처리 실패');
    return await response.json();
};
// 레거시 alias
export const markInviteRead = markMessageRead;

/**
 * 메시지 발송 — 'M' 쪽지 또는 'A' 공지.
 * @param recipientUsrids: 다중 수신자 usrid 배열
 * @param msgTcd: 'M' (default) 또는 'A'
 */
export const sendMessage = async ({ recipientUsrids, msgTitle, msgBody, parentMsgId, msgTcd }) => {
    // COMM 전환: 쪽지(M)는 DM 방으로, 공지(A)는 테넌트 공지방으로.
    // parentMsgId(답장 원본)는 COMM 에서 방 맥락이 대신한다.
    if (isCommTransport() && (!msgTcd || msgTcd === 'M')) {
        return await commClient.send({ recipientUsrids, msgTitle, msgBody, parentMsgId });
    }
    if (isCommTransport() && msgTcd === 'A') {
        // 권한 없음(403)은 서버 사유가 그대로 throw 된다 — 조용히 레거시로 돌리지 않는다
        return await commClient.sendNotice([msgTitle, msgBody].filter(Boolean).join('\n'));
    }
    const response = await fetch(`${REACT_APP_BE_API}/qbase/api/sendMessage`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            recipientUsrids,
            msgTitle,
            msgBody,
            parentMsgId,
            msgTcd: msgTcd || 'M',
        }),
    });
    if (!response.ok) throw new Error('메시지 발송 실패');
    return await response.json();
    // 응답: { sent, ids }
};

/**
 * 메시지 1건 + 답장 원본 동시 조회.
 */
export const fetchMessageDetail = async (msgId) => {
    if (isCommTransport() && isCommMsgId(msgId)) {
        return await commClient.fetchDetail(msgId);
    }
    const response = await fetch(`${REACT_APP_BE_API}/qbase/api/messageDetail`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ msgId }),
    });
    if (!response.ok) throw new Error('메시지 상세 조회 실패');
    return await response.json();
    // 응답: { message, parent | null }
};

export const deleteCloudModel = async (mdlrmdlId, userId) => {

    const modelInfoData = {
        mdlrmdlId: mdlrmdlId,
        mdlrmdlTypNm: "데이터모델",
    };
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/save?저장구분=사용중지&사용자ID=${userId}&모델정보=${encodeURIComponent(JSON.stringify(modelInfoData))}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                modelInfo: modelInfoData,
                dgrmCn: ''
            }),
        });
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('모델 삭제 중 오류 발생:', error);
        throw error;
    }
};

export const resetCloudModelStatus = async (mdlrmdlId, userId) => {

    const modelInfoData = {
        mdlrmdlId: mdlrmdlId,
        mdlrmdlTypNm: "데이터모델",
    };
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/save?저장구분=상태초기화&사용자ID=${userId}&모델정보=${encodeURIComponent(JSON.stringify(modelInfoData))}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                modelInfo: modelInfoData,
                dgrmCn: ''
            }),
        });
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('모델 상태초기화 중 오류 발생:', error);
        throw error;
    }
};

// 표준사전

export const registerWord = async (word, userId, stdNm) => {
    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/std/wdSave`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            wdNm: word.wordName,
            wdAbbrNm: word.physicalName,
            meanCn: word.description,
            wdEngCn: word.englishWord,
            usrid: userId,
            표준명: stdNm,
            // 필요한 경우 추가 필드
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to register word');
    }

    return await response.json();
};

export const registerInfotype = async (infotypeData) => {
    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/std/inftpSave`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(infotypeData),
    });
    return await response.json();
};

/**
 * 시퀀스 ID 채번 (POST /qcore/home/qbase/nextid)
 * @param {object} params  채번 파라미터
 *   VW_QBASE_SQ: { qbaseSysId: 'QSY0000005', dmnNm: '모델러모델공유그룹ID' }
 *   VW_SQ:       { sysNm: '시스템', dmnNm: '큐베이스테스트ID' }
 * @returns {string} 생성된 ID
 */
export const fetchNextId = async (params) => {
    const response = await fetch(`${REACT_APP_BE_API}/qbase/nextid`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
    });
    if (!response.ok) throw new Error('ID 채번 실패');
    const data = await response.json();
    return data.nextId;
};

/**
 * 참여번호 생성 (숫자 6자리)
 */
export const generateJoinCode = () => {
    return String(Math.floor(100000 + Math.random() * 900000));
};

/**
 * 참여번호로 그룹 조회
 */
export const fetchShareGroupByJoinCode = async (joinCode) => {
    const response = await fetch(
        `${REACT_APP_BE_API}/mdlrmdl/shrgrpByJoinCd?참여번호=${encodeURIComponent(joinCode)}`,
        { credentials: 'include' }
    );
    if (!response.ok) throw new Error('참여번호 조회 실패');
    const data = await response.json();
    return data.data || [];
};

/**
 * 사용중인 전체 공유그룹 목록 조회
 */
export const fetchPublicShareGroups = async () => {
    const response = await fetch(
        `${REACT_APP_BE_API}/mdlrmdl/publicShrgrpLst`,
        { credentials: 'include' }
    );
    if (!response.ok) throw new Error('공개 그룹 목록 조회 실패');
    const data = await response.json();
    return data.publicShrgrpLst || data.data || [];
};

/**
 * 공유그룹 저장 (saveCmm 형식)
 * 신규: nextId로 ID 채번 후 createdRows, 수정: updatedRows
 */
export const saveShareGroup = async (shareGroupData) => {
    const { mdlrmdlShrgrpId, shrgrpNm, shrgrpScd, shrgrpTcd, mgtUsrid, joinCd } = shareGroupData;
    const isNew = !mdlrmdlShrgrpId;

    let id = mdlrmdlShrgrpId;
    if (isNew) {
        id = await fetchNextId({ qbaseSysId: 'QSY0000005', dmnNm: '모델러모델공유그룹ID' });
    }

    const row = {
        MDLRMDL_SHRGRP_ID: id,
        SHRGRP_NM: shrgrpNm,
        SHRGRP_SCD: shrgrpScd,
        SHRGRP_TCD: shrgrpTcd,
        MGT_USRID: mgtUsrid,
        JOIN_CD: joinCd || '',
    };

    const body = {
        createdRows: isNew ? [row] : [],
        updatedRows: isNew ? [] : [row],
        deletedRows: [],
    };

    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveShrgrp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('Failed to save share group');
    const result = await response.json();
    // saveCmm은 {isSuccess:'Y'} 반환 → 프론트 호환을 위해 mdlrmdlShrgrpId 추가
    result.mdlrmdlShrgrpId = id;
    return result;
};

export const fetchShareGroups = async (userId) => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/shrgrpLst?사용자ID=${userId}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        const data = await response.json();
        return data.shrgrpLst;
    } catch (error) {
        console.error('Error fetching share groups:', error);
        throw error;
    }
};

export const fetchAllShareGroupUsers = async () => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/shrgrpUserLst`, {
            credentials: 'include'
        });
        if (!response.ok) throw new Error('서버 응답이 실패했습니다');
        const data = await response.json();
        return data.shrgrpUserLst;
    } catch (error) {
        console.error('Error fetching all share group users:', error);
        throw error;
    }
};

export const fetchShareGroupUsers = async (mdlrmdlShrgrpId) => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/shrgrpUserLst?모델러모델공유그룹ID=${mdlrmdlShrgrpId}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        const data = await response.json();
        return data.shrgrpUserLst;
    } catch (error) {
        console.error('Error fetching share group users:', error);
        throw error;
    }
};

export const fetchShareGroupAssignments = async (mdlrmdlShrgrpId) => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/shrgrpAsmtLst?모델러모델공유그룹ID=${mdlrmdlShrgrpId}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        const data = await response.json();
        return data.shrgrpAsmtLst;
    } catch (error) {
        console.error('Error fetching share group assignments:', error);
        throw error;
    }
};

export const fetchUsers = async (searchKeyword = '') => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/modelerUserLst?검색어=${encodeURIComponent(searchKeyword)}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        const data = await response.json();
        return data.modelerUserLst;
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

/** saveCmm 응답 검증 헬퍼 */
const assertSaveCmmSuccess = async (response, context) => {
    if (!response.ok) throw new Error(`${context}: HTTP ${response.status}`);
    const result = await response.json();
    if (result.isSuccess !== 'Y') throw new Error(`${context}: ${result.resultMessage || '저장 실패'}`);
    return result;
};

/** saveCmm POST 공통 옵션 */
const saveCmmOpts = (body) => ({
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

/**
 * 공유그룹 사용자 일괄 저장 (saveCmm 형식)
 * @param {string} shrgrpId - 공유그룹 ID
 * @param {object} changes - { created: [{usrid, joinScd, mngUsrid}], updated: [{usrid, joinScd}], deleted: [{usrid}] }
 *
 * JOIN_SCD (참여상태코드):
 *   001=참여중, 002=승인대기, 003=승인불허, 004=승인철회
 */
export const batchSaveShareGroupUsers = async (shrgrpId, { created = [], updated = [], deleted = [] }) => {
    const body = {
        createdRows: created.map(u => ({
            MDLRMDL_SHRGRP_ID: shrgrpId,
            USRID: u.usrid,
            JOIN_SCD: u.joinScd || '001',
            MNG_USRID: u.mngUsrid,
        })),
        updatedRows: updated.map(u => ({
            MDLRMDL_SHRGRP_ID: shrgrpId,
            USRID: u.usrid,
            JOIN_SCD: u.joinScd,
        })),
        deletedRows: deleted.map(u => ({
            MDLRMDL_SHRGRP_ID: shrgrpId,
            USRID: u.usrid,
        })),
    };
    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveShrgrpUser`, saveCmmOpts(body));
    return await assertSaveCmmSuccess(response, '공유그룹 사용자 저장');
};

/**
 * 공유그룹 과제 일괄 저장 (saveCmm 형식)
 * @param {string} shrgrpId - 공유그룹 ID
 * @param {object} changes - { created: [{shrgrpAsmtNm, shrgrpAsmtScd}], updated: [...], deleted: [...] }
 */
export const batchSaveShareGroupAssignments = async (shrgrpId, { created = [], updated = [], deleted = [] }) => {
    const toRow = (a) => ({
        MDLRMDL_SHRGRP_ID: shrgrpId,
        SHRGRP_ASMT_NM: a.shrgrpAsmtNm,
        SHRGRP_ASMT_SCD: a.shrgrpAsmtScd,
    });
    const body = {
        createdRows: created.map(toRow),
        updatedRows: updated.map(toRow),
        deletedRows: deleted.map(toRow),
    };
    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveShrgrpAsmt`, saveCmmOpts(body));
    return await assertSaveCmmSuccess(response, '공유그룹 과제 저장');
};

/**
 * 공유그룹 + 하위 데이터 일괄 삭제 (saveCmm 형식)
 * @param {string} shrgrpId - 삭제할 공유그룹 ID
 * @param {Array} users - 삭제할 사용자 목록 [{usrid}]
 * @param {Array} assignments - 삭제할 과제 목록 [{shrgrpAsmtNm}]
 */
export const deleteShareGroupCascade = async (shrgrpId, users = [], assignments = []) => {
    // 1) 하위 사용자 삭제
    if (users.length > 0) {
        await batchSaveShareGroupUsers(shrgrpId, { deleted: users });
    }
    // 2) 하위 과제 삭제
    if (assignments.length > 0) {
        await batchSaveShareGroupAssignments(shrgrpId, { deleted: assignments });
    }
    // 3) 그룹 자체 삭제
    const body = { createdRows: [], updatedRows: [], deletedRows: [{ MDLRMDL_SHRGRP_ID: shrgrpId }] };
    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveShrgrp`, saveCmmOpts(body));
    return await assertSaveCmmSuccess(response, '공유그룹 삭제');
};

/**
 * 공유그룹 사용자 단건 추가/삭제 (UserShareGroups.js 등에서 사용)
 * 저장구분: '추가' → created, '삭제' → deleted
 * joinScd: 001=참여중, 002=승인대기, 003=승인불허, 004=승인철회
 */
export const saveShareGroupUser = async (userData) => {
    const { mdlrmdlShrgrpId, usrid, joinScd, mngUsrid } = userData;
    const saveType = userData['저장구분'];
    const entry = { usrid, joinScd, mngUsrid };
    return await batchSaveShareGroupUsers(mdlrmdlShrgrpId, {
        created: saveType === '추가' ? [entry] : [],
        deleted: saveType === '삭제' ? [entry] : [],
    });
};

/**
 * 공유그룹 과제 단건 추가/수정/삭제
 * 저장구분: '추가' → created, '수정' → updated, '삭제' → deleted
 */
export const saveShareGroupAssignment = async (assignmentData) => {
    const { mdlrmdlShrgrpId, shrgrpAsmtNm, shrgrpAsmtScd } = assignmentData;
    const saveType = assignmentData['저장구분'];
    const entry = { shrgrpAsmtNm, shrgrpAsmtScd };
    return await batchSaveShareGroupAssignments(mdlrmdlShrgrpId, {
        created: saveType === '추가' ? [entry] : [],
        updated: saveType === '수정' ? [entry] : [],
        deleted: saveType === '삭제' ? [entry] : [],
    });
};

/**
 * 클라우드에 모델 저장 (미리보기 이미지 포함)
 *
 * @param {Object} modelInfo - 모델 정보
 * @param {string} modelInfo.mdlrmdlId - 모델 ID (신규일 경우 빈 문자열)
 * @param {string} modelInfo.dgrmVerNo - 다이어그램 버전
 * @param {string} modelInfo.modelName - 모델명
 * @param {string} modelInfo.modelDescription - 모델 설명
 * @param {string} modelInfo.openMethod - 열기 방식 ('클라우드저장' | '클라우드열기' | ...)
 * @param {Array} modelInfo.nodes - 노드 배열
 * @param {Array} modelInfo.edges - 엣지 배열
 * @param {Object} modelInfo.attrs - 속성 객체
 * @param {string} modelInfo.projectNm - 프로젝트명
 * @param {string} userId - 사용자 ID
 * @param {string} [imageData] - Base64 인코딩된 미리보기 이미지 (data:image/png;base64, 접두어 제외)
 * @returns {Promise<Object>} - { mdlrmdlId, dgrmVerNo, imagePath }
 */
export const saveModelToCloudApi = async (modelInfo, userId, imageData = null) => {
    const { mdlrmdlId, dgrmVerNo, modelName, modelDescription, openMethod } = modelInfo;

    const actionType = mdlrmdlId && (openMethod === '클라우드저장' || openMethod === '클라우드열기') ? 'update' : 'new';

    const dgrmCnObject = {
        // 저장 본문에 스키마 버전을 찍는다 — 읽는 쪽이 변환 여부를 판단하는 유일한 근거
        [SCHEMA_VERSION_KEY]: CURRENT_SCHEMA_VERSION,
        nodes: modelInfo.nodes,
        edges: modelInfo.edges,
        attrs: modelInfo.attrs,
        exclusiveGroups: modelInfo.exclusiveGroups || [], // 배타적 관계 그룹
        subtypeGroups: modelInfo.subtypeGroups || [], // 서브타입/수퍼타입 그룹
        viewport: modelInfo.viewport || null, // 뷰포트 정보 (x, y, zoom)
        modelConfig: modelInfo.modelConfig || {}, // 모델 설정 (다이어그램 모드 등)
    };
    const dgrmCn = JSON.stringify(dgrmCnObject);

    // 노드 개수 계산 (memoNode 제외)
    const nodesCnt = modelInfo.nodes?.filter(node => node.type !== 'memoNode').length || 0;

    // mdlrmdlAddCn: 모델 추가 정보 (nodesCnt, projectNm, srcMdlrmdlId 등)
    // srcMdlrmdlId: 복제본의 경우 원본 모델 ID. 강사가 학생 제출본의 출처를 추적하기 위함 (G2).
    //               신규 모델/직접 작성은 null 유지.
    const mdlrmdlAddCnObject = {
        nodesCnt: nodesCnt,
        projectNm: modelInfo.projectNm || null,
        srcMdlrmdlId: modelInfo.sourceMdlrmdlId || null,
    };
    const mdlrmdlAddCn = JSON.stringify(mdlrmdlAddCnObject);

    // 백엔드 가이드에 맞춘 모델 정보 객체
    // mdlrmdlAddCn에 projectNm이 포함되어 있으므로 별도 projectNm 필드는 불필요
    const modelInfoData = {
        mdlrmdlId: actionType === 'new' ? '' : mdlrmdlId,
        dgrmVerNo: actionType === 'new' ? "100" : dgrmVerNo,
        modelNm: modelName,
        modelDc: modelDescription,
        mdlrmdlTypNm: "데이터모델",
        mdlrmdlAddCn: mdlrmdlAddCn,  // JSON 문자열: {"nodesCnt":15,"projectNm":"CDW"}
    };


    // 백엔드 가이드: URL 쿼리 파라미터로 모델정보 전달
    // 이미지 데이터가 있으면 쿼리 파라미터에 추가
    const params = new URLSearchParams({
        '저장구분': actionType === 'new' ? '신규' : '갱신',
        '사용자ID': userId,
        '모델정보': JSON.stringify(modelInfoData)
    });

    // 미리보기 이미지 데이터 추가 (있는 경우)
    if (imageData) {
        // data:image/png;base64, 접두어가 있으면 제거
        const cleanImageData = imageData.replace(/^data:image\/\w+;base64,/, '');
        params.append('이미지데이터', cleanImageData);
    }

    const apiUrl = `${REACT_APP_BE_API}/mdlrmdl/save?${params.toString()}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: dgrmCn,  // 다이어그램 내용 (nodes, edges, attrs) - 백엔드 가이드에 따라 JSON 직접 전송
        });

        // 401/403 응답 시 세션 만료 처리
        if (response.status === 401 || response.status === 403) {
            console.warn(`⚠️ 세션 만료 감지 (${response.status}): 저장 API 호출 중 인증 오류`);
            const sessionExpiredError = new Error('세션이 만료되었습니다. 다시 로그인해주세요.');
            sessionExpiredError.code = 'SESSION_EXPIRED';
            throw sessionExpiredError;
        }

        // 큐베이스 점검(쓰기 동결) 거부 — 자동 재전송하지 않는다 (중복 저장 위험). 화면의 모델은 그대로 둔다
        await throwIfMaintenanceResponse(response);

        const result = await response.json();
        // 응답: { mdlrmdlId, dgrmVerNo, imagePath }
        return result;
    } catch (error) {
        console.error('모델 클라우드 저장 중 오류 발생:', error);
        throw toMaintenanceErrorIfSwitching(error);
    }
};


export const getCloudModels = async (userId) => {
    try {

        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/mdlrmdlLst?모델러모델유형명=데이터모델&사용자ID=${userId}`, {
            credentials: 'include'  // ⭐ 세션 쿠키 전달
        });


        // 세션 만료로 로그인 페이지로 리다이렉트된 경우 감지
        // 조건: HTML 응답 + (302 리다이렉트 또는 URL에 로그인 경로 포함)
        const contentType = response.headers.get('content-type');
        const isHtmlResponse = contentType && contentType.includes('text/html');
        const isLoginRedirect = response.url.includes('openLogin') || response.url.includes('login');

        if (isHtmlResponse && isLoginRedirect) {
            // HTML 응답 + 로그인 URL = 세션 만료
            console.warn('⚠️ 세션 만료 감지: HTML 응답 수신 (로그인 페이지로 리다이렉트됨)');
            const sessionExpiredError = new Error('세션이 만료되었습니다. 다시 로그인해주세요.');
            sessionExpiredError.code = 'SESSION_EXPIRED';
            throw sessionExpiredError;
        }

        // HTML 응답이지만 로그인 리다이렉트가 아닌 경우
        // 일부 백엔드는 HTML content-type으로 JSON을 반환하기도 함 (잘못된 설정)
        if (isHtmlResponse) {
            const responseText = await response.text();
            console.warn('⚠️ HTML content-type 응답 수신:', {
                status: response.status,
                url: response.url,
                contentType,
                responsePreview: responseText.substring(0, 500)  // 처음 500자만 로그
            });

            // HTML이지만 실제로 JSON인 경우 파싱 시도
            try {
                const jsonData = JSON.parse(responseText);

                // mdlrmdlAddCn에서 projectNm, nodesCnt, srcMdlrmdlId 추출하여 모델 객체에 추가
                const modelsWithProjectNm = (jsonData.mdlrmdlLst || []).map(model => {
                    let projectNm = '';
                    let nodesCnt = 0;
                    let srcMdlrmdlId = null;

                    if (model.mdlrmdlAddCn) {
                        try {
                            const addCnObj = typeof model.mdlrmdlAddCn === 'string'
                                ? JSON.parse(model.mdlrmdlAddCn)
                                : model.mdlrmdlAddCn;
                            projectNm = addCnObj.projectNm || '';
                            nodesCnt = addCnObj.nodesCnt || 0;
                            srcMdlrmdlId = addCnObj.srcMdlrmdlId || null;
                        } catch (e) {
                            console.warn('mdlrmdlAddCn 파싱 실패:', model.mdlrmdlAddCn, e);
                        }
                    }

                    return { ...model, projectNm, nodesCnt, srcMdlrmdlId };
                });

                return modelsWithProjectNm;
            } catch (parseError) {
                // 진짜 HTML인 경우 (로그인 페이지 등)
                if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
                    console.warn('⚠️ 세션 만료 또는 인증 오류: HTML 페이지 반환됨');
                    const sessionExpiredError = new Error('세션이 만료되었습니다. 다시 로그인해주세요.');
                    sessionExpiredError.code = 'SESSION_EXPIRED';
                    throw sessionExpiredError;
                }
                throw new Error(`서버 응답을 파싱할 수 없습니다: ${responseText.substring(0, 200)}`);
            }
        }

        // 403 에러 상세 로깅
        if (!response.ok) {
            let errorDetail;

            if (contentType && contentType.includes('application/json')) {
                errorDetail = await response.json();
            } else {
                errorDetail = await response.text();
            }

            console.error('getCloudModels 실패:', {
                status: response.status,
                statusText: response.statusText,
                errorDetail,
                url: response.url
            });

            throw new Error(`서버 응답 오류 (${response.status}): ${JSON.stringify(errorDetail)}`);
        }

        const result = await response.json();

        // mdlrmdlAddCn에서 projectNm, nodesCnt, srcMdlrmdlId 추출하여 모델 객체에 추가
        const modelsWithProjectNm = (result.mdlrmdlLst || []).map(model => {
            let projectNm = '';
            let nodesCnt = 0;
            let srcMdlrmdlId = null;

            // mdlrmdlAddCn 파싱 (JSON 문자열 형식: {"nodesCnt": 15, "projectNm": "인하대병원CDW", "srcMdlrmdlId": "MDL..."})
            if (model.mdlrmdlAddCn) {
                try {
                    const addCnObj = typeof model.mdlrmdlAddCn === 'string'
                        ? JSON.parse(model.mdlrmdlAddCn)
                        : model.mdlrmdlAddCn;
                    projectNm = addCnObj.projectNm || '';
                    nodesCnt = addCnObj.nodesCnt || 0;
                    srcMdlrmdlId = addCnObj.srcMdlrmdlId || null;
                } catch (e) {
                    console.warn('mdlrmdlAddCn 파싱 실패:', model.mdlrmdlAddCn, e);
                }
            }

            return {
                ...model,
                projectNm,    // mdlrmdlAddCn에서 추출한 프로젝트명
                nodesCnt,     // mdlrmdlAddCn에서 추출한 노드 개수
                srcMdlrmdlId, // 복제본의 원본 모델 ID (G2 — 강사가 학생 제출본 출처 추적용)
            };
        });

        // 디버깅: 모델 목록 상세 정보 출력
        if (modelsWithProjectNm.length > 0) {
            // 첫 번째 모델의 전체 필드 구조 출력

            modelsWithProjectNm.slice(0, 5).forEach((model, index) => {
            });
        }

        return modelsWithProjectNm;
    } catch (error) {
        console.error('모델 목록을 가져오는 중 오류 발생:', error);
        throw error;
    }
};


// Use callApi for consistent API management
export const openCloudModel = async (modelId, userId) => {
    try {
        const response = await callApi('openMdlrmdl', {
            mdlrmdlId: modelId,
            usrid: userId
        });

        // mdlrmdlAddCn에서 projectNm 추출 (백엔드가 직접 projectNm을 반환하지 않는 경우 대비)
        if (response && !response.projectNm && response.mdlrmdlAddCn) {
            try {
                const addCnObj = typeof response.mdlrmdlAddCn === 'string'
                    ? JSON.parse(response.mdlrmdlAddCn)
                    : response.mdlrmdlAddCn;
                response.projectNm = addCnObj.projectNm || '';
            } catch (e) {
                console.warn('mdlrmdlAddCn 파싱 실패:', response.mdlrmdlAddCn, e);
                response.projectNm = '';
            }
        }

        return response;
    } catch (error) {
        console.error('모델을 불러오는 중 오류 발생:', error);
        throw error;
    }
};

export const getShareGroupAsmtModelByMdlrmdlId = async (mdlrmdlId) => {

    try {

        const params = new URLSearchParams({
            '모델러모델공유그룹ID': '',
            '공유그룹과제명': '',
            '모델러모델ID': mdlrmdlId
        });
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/shrgrpAsmtModelLst?${params}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        const data = await response.json();
        return data.shrgrpAsmtModelLst;
    } catch (error) {
        console.error('공유그룹과제 조회 중 오류 발생:', error);
        throw error;
    }
};

//export const fetchSharedModels = async (groupId, assignmentNm, searchTerm) => {
export const fetchSharedModels = async (groupId, assignmentNm, modelId) => {

    try {

        const params = new URLSearchParams({
            '모델러모델공유그룹ID': groupId,
            '공유그룹과제명': assignmentNm,
            '모델러모델ID': modelId,
        });
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/shrgrpAsmtModelLst?${params}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        const data = await response.json();
        return data.shrgrpAsmtModelLst;
    } catch (error) {
        console.error('공유그룹과제 조회 중 오류 발생:', error);
        throw error;
    }
};

/**
 * 공유그룹 과제 모델 등록/수정/삭제 (saveCmm 형식)
 * saveType: '신규' → createdRows, '수정' → updatedRows, '삭제' → deletedRows
 */
export const saveShareGroupAsmtModel = async (saveData) => {
    const { mdlrmdlShrgrpId, shrgrpAsmtNm, mdlrmdlId, shareTcd, cmtsCn, prvwFlpth, reviewCn, saveType } = saveData;

    const row = {
        MDLRMDL_SHRGRP_ID: mdlrmdlShrgrpId,
        SHRGRP_ASMT_NM: shrgrpAsmtNm,
        MDLRMDL_ID: mdlrmdlId,
        PRVW_FLPTH: prvwFlpth,
    };
    if (saveType !== '삭제') {
        row.SHARE_TCD = shareTcd;
        row.CMTS_CN = cmtsCn;
        // G3: 강사 평가 JSON (REVIEW_CN). 미지정 시 컬럼 보냄 안 함 (기존 값 보존).
        if (reviewCn !== undefined) row.REVIEW_CN = reviewCn;
    }

    const body = {
        createdRows: saveType === '신규' ? [row] : [],
        updatedRows: saveType === '수정' ? [row] : [],
        deletedRows: saveType === '삭제' ? [row] : [],
    };


    const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveShrgrpAsmtModel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const errText = await response.text().catch(() => '(본문 읽기 실패)');
        console.error('[saveShareGroupAsmtModel] 실패 응답 본문:', errText);
        throw new Error('서버 응답이 실패했습니다 — HTTP ' + response.status);
    }
    const json = await response.json();
    return json;
};

/**
 * G3: 강사 평가 저장.
 * 학생 제출본 (MDLRMDL_SHRGRP_ASMT_MODEL 행) 에 REVIEW_CN(JSON) 만 갱신.
 * 다른 컬럼(SHARE_TCD, CMTS_CN) 은 보존됨.
 *
 * @param {Object} reviewData
 *   - mdlrmdlShrgrpId / shrgrpAsmtNm / mdlrmdlId : 대상 행 식별 PK
 *   - score      : number | null     (0~100)
 *   - reviewCmt  : string | null     (강사 코멘트)
 *   - gradeScd   : string            ('001' 평가완료 / '002' 재제출요청 / '003' 수정요청 / '004' 미평가)
 *   - reviewerUsrid : string         (강사 본인 usrid)
 */
export const saveSharedModelReview = async ({ mdlrmdlShrgrpId, shrgrpAsmtNm, mdlrmdlId, score, reviewCmt, gradeScd, reviewerUsrid }) => {
    const reviewObj = {
        score: (score == null || score === '') ? null : Number(score),
        reviewCmt: reviewCmt || '',
        gradeScd: gradeScd || '004',
        reviewerUsrid: reviewerUsrid || '',
        reviewedAt: new Date().toISOString(),
    };
    return saveShareGroupAsmtModel({
        mdlrmdlShrgrpId,
        shrgrpAsmtNm,
        mdlrmdlId,
        // SHARE_TCD / CMTS_CN 은 변경하지 않음 — 백엔드 saveCmm 이 누락 필드는 미변경 처리
        shareTcd: undefined,
        cmtsCn: undefined,
        prvwFlpth: undefined,
        reviewCn: JSON.stringify(reviewObj),
        saveType: '수정',
    });
};


export const fetchModelShareLst = async (userId, modelId) => {
    try {
      const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/modelShareLst?사용자ID=${userId}&모델러모델ID=${modelId}`, {
          credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('서버 응답이 실패했습니다');
      }
      const data = await response.json();
      return data.modelShareLst;
    } catch (error) {
      console.error('모델 공유 내역 조회 중 오류 발생:', error);
      throw error;
    }
  };

  export const saveModelImage = async (mdlrmdlId, imageData) => {
    try {
      const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveModelImage`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mdlrmdlId: mdlrmdlId,
          imageData: imageData
        }),
      });
      if (!response.ok) {
        throw new Error('이미지 저장에 실패했습니다');
      }
      return await response.json();
    } catch (error) {
      console.error('모델 이미지 저장 중 오류 발생:', error);
      throw error;
    }
  };  


  /**
   * @deprecated 2026-06.
   *   백엔드 /mdlrmdl/getModelPreview 가 Stage 3 에서 주석 처리됨 (이미지 파일 저장도 비활성).
   *   대신 클라이언트가 dgrmCn 을 직접 받아 SVG 로 렌더하는 useModelThumbnail + ModelThumbnail 사용.
   *   - 위치: src/utils/hooks/useModelThumbnail.js, src/components/shared/ModelThumbnail.js
   *   - 호출 시 항상 실패 응답.
   */
  export const fetchModelPreview = async (userId, mdlrmdlId, prvwFlpth) => {
    try {
      const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/getModelPreview`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          usrid: userId,
          mdlrmdlId: mdlrmdlId,
          prvwFlpth: prvwFlpth
        }),
      });
  
      if (!response.ok) {
        throw new Error('서버 응답이 실패했습니다.');
      }
  
      const data = await response.json();
  
      if (!data.imageData) {
        throw new Error('이미지 데이터가 없습니다.');
      }
  
      const imageUrl = `data:${data.contentType};base64,${data.imageData}`;
      return {
        imageUrl: imageUrl,
        fileName: data.fileName
      };
    } catch (error) {
      console.error('미리보기 이미지를 가져오는 중 오류 발생:', error);
      throw new Error('이미지를 가져오는데 실패했습니다.');
    }
  };


  export const saveMdlrmdlLive = async (saveData) => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveMdlrmdlLive`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(saveData),
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        return await response.json();
    } catch (error) {
        console.error('라이브 모델 저장 중 오류 발생:', error);
        throw error;
    }
  };

  export const saveMdlrmdlLiveUser = async (saveData) => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/saveMdlrmdlLiveUser`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(saveData),
        });
        if (!response.ok) {
            throw new Error('서버 응답이 실패했습니다');
        }
        return await response.json();
    } catch (error) {
        console.error('라이브 모델 사용자 저장 중 오류 발생:', error);
        throw error;
    }
  };

/**
 * 회원 탈퇴 — 백엔드에서 사용자 계정 및 관련 데이터를 영구 삭제.
 * 호출 후 클라이언트는 즉시 로그아웃 + user-scoped 저장소 정리 필요.
 *
 * @param {string} userId - 탈퇴할 사용자 ID
 * @returns {Promise<Object>} { isSuccess: 'Y'|'N', message?: string }
 */
export const deleteUserAccount = async (userId) => {
    if (!userId) throw new Error('userId 가 없습니다');
    const response = await fetch(`${REACT_APP_BE_API}/qbase/api/deleteUser`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
        throw new Error(`회원 탈퇴 요청 실패 (HTTP ${response.status})`);
    }
    return await response.json();
};